// Defaults & Workout rebuild — workflow-spine workspace (replaces the ten
// equal accordion cards):
//   • No active case  → calm page: empty state + case history + the DW-M1
//     monthly report card (clean bill of health).
//   • Active case     → status banner (stage chip ladder + money strip +
//     Transition state) → stage-action rail → chronological timeline spine
//     (filterable; entries open the right drawer; ?artifact= deep links)
//     beside the compact side-rail cards → case history → the cure-period
//     sticky panel.
// All action modals mount HERE at workspace level (the pre-rebuild freeze:
// modals inside collapsed <details> scroll-locked the page invisibly).

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useUserTimeZone } from "@/utils/datetime";
import {
  useCollateralRecoveries,
  useCurePeriod,
  useDACAEvents,
  useEnforcerEngagementsForCase,
  useForbearance,
  useLegalActions,
  useLoanDefaultCases,
  useRemediationPlans,
  useWorkoutCosts,
  useWorkoutEvents,
  useWorkoutRecoveries,
} from "@/hooks/portfolioAdmin/usePr4b";
import { useLoanReportRequests } from "@/hooks/portfolioAdmin/useReportRequests";
import { NoActiveDefaultEmptyState } from "../defaults/NoActiveDefaultEmptyState";
import { OpenDefaultCaseModal } from "../defaults/OpenDefaultCaseModal";
import { HistoricalDefaultsList } from "../defaults/HistoricalDefaultsList";
import { CurePeriodStickyPanel } from "../defaults/CurePeriodStickyPanel";
import { CaseStatusBanner } from "../defaults/CaseStatusBanner";
import { StageActionRail } from "../defaults/StageActionRail";
import { CaseTimeline } from "../defaults/CaseTimeline";
import { ArtifactDrawer } from "../defaults/ArtifactDrawer";
import { SideRailCards } from "../defaults/SideRailCards";
import { DwReportCard } from "../defaults/DwReportCard";
import { TimeLogSection } from "../defaults/TimeLogSection";
import {
  CaseActionModals,
  type WorkspaceAction,
} from "../defaults/CaseActionModals";
import {
  buildCaseTimeline,
  parseTimelineKey,
  type CaseArtifacts,
} from "../defaults/timeline";
import type { CurePeriodDto, DefaultCaseDto } from "@/api/portfolioAdmin/pr4bTypes";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";

interface DefaultsWorkoutTabProps {
  loan: LoanSummaryDto;
}

const CLOSED_STATES = new Set(["closed-final"]);

export function DefaultsWorkoutTab({ loan }: DefaultsWorkoutTabProps) {
  const auth = useAuth();
  const canOpenManually =
    auth.hasRoleCode("B3") ||
    auth.hasRoleCode("A3") ||
    auth.hasRoleCode("E1") ||
    auth.hasRoleCode("ADMIN") ||
    auth.hasPermission("Defaults.Manage");

  const [params, setParams] = useSearchParams();
  const caseParam = params.get("case");

  const { data, isLoading } = useLoanDefaultCases(loan.id);
  const cases = data ?? [];

  const { active, historical } = useMemo(() => {
    const a: DefaultCaseDto[] = [];
    const h: DefaultCaseDto[] = [];
    for (const c of cases) {
      if (CLOSED_STATES.has(c.status)) h.push(c);
      else a.push(c);
    }
    a.sort(
      (x, y) =>
        new Date(y.triggerDate).getTime() - new Date(x.triggerDate).getTime(),
    );
    h.sort(
      (x, y) =>
        new Date(y.closedDate ?? y.triggerDate).getTime() -
        new Date(x.closedDate ?? x.triggerDate).getTime(),
    );
    return { active: a, historical: h };
  }, [cases]);

  const [openModal, setOpenModal] = useState(false);

  // If a ?case=<id> deep-link points to one of the cases, prefer it;
  // otherwise pick the first active case (or null).
  const deepLinked: DefaultCaseDto | undefined = caseParam
    ? cases.find((c) => c.id === caseParam)
    : undefined;
  const displayCase: DefaultCaseDto | null = deepLinked ?? active[0] ?? null;

  const selectCase = (id: string) => {
    const u = new URLSearchParams(params);
    u.set("case", id);
    u.delete("artifact");
    setParams(u, { replace: true });
  };

  if (isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">
          Loading default cases…
        </p>
      </Card>
    );
  }

  if (!displayCase) {
    return (
      <div
        className="flex flex-col gap-space-3"
        data-testid="defaults-workout-tab"
      >
        <NoActiveDefaultEmptyState
          canOpenManually={canOpenManually}
          onOpenManually={() => setOpenModal(true)}
        />
        <DwReportCard loanId={loan.id} />
        <HistoricalDefaultsList cases={historical} onSelect={selectCase} />
        {openModal && (
          <OpenDefaultCaseModal
            loanId={loan.id}
            open={openModal}
            onClose={() => setOpenModal(false)}
          />
        )}
      </div>
    );
  }

  return (
    <DefaultsWorkoutCaseWorkspace
      loanId={loan.id}
      defaultCase={displayCase}
      historical={historical}
      onSelectHistorical={selectCase}
    />
  );
}

interface CaseWorkspaceProps {
  loanId: string;
  defaultCase: DefaultCaseDto;
  historical: DefaultCaseDto[];
  onSelectHistorical: (id: string) => void;
}

function DefaultsWorkoutCaseWorkspace({
  loanId,
  defaultCase,
  historical,
  onSelectHistorical,
}: CaseWorkspaceProps) {
  const tz = useUserTimeZone();
  const [params, setParams] = useSearchParams();
  const [action, setAction] = useState<WorkspaceAction | null>(null);

  const caseId = defaultCase.id;
  const events = useWorkoutEvents(caseId);
  const curePeriods = useCurePeriod(caseId);
  const plans = useRemediationPlans(caseId);
  const forbearance = useForbearance(caseId);
  const daca = useDACAEvents(caseId);
  const legalActions = useLegalActions(caseId);
  const engagements = useEnforcerEngagementsForCase(caseId);
  const collateralRecoveries = useCollateralRecoveries(caseId);
  const costs = useWorkoutCosts(caseId);
  const recoveries = useWorkoutRecoveries(caseId);
  // Report requests are LOAN-scoped, not case-scoped — `buildCaseTimeline`
  // narrows them to this case. Same endpoint the Financial Reporting tab
  // reads, so on a loan where both tabs have been opened this is a cache hit.
  const reportRequests = useLoanReportRequests(loanId);

  const artifacts: CaseArtifacts = useMemo(
    () => ({
      defaultCase,
      events: events.data ?? [],
      curePeriods: Array.isArray(curePeriods.data) ? curePeriods.data : [],
      plans: plans.data ?? [],
      forbearance: forbearance.data ?? [],
      daca: daca.data ?? [],
      legalActions: legalActions.data ?? [],
      engagements: engagements.data ?? [],
      collateralRecoveries: collateralRecoveries.data ?? [],
      costs: costs.data ?? [],
      recoveries: recoveries.data ?? [],
      reportRequests: reportRequests.data?.requests ?? [],
    }),
    [
      defaultCase,
      events.data,
      curePeriods.data,
      plans.data,
      forbearance.data,
      daca.data,
      legalActions.data,
      engagements.data,
      collateralRecoveries.data,
      costs.data,
      recoveries.data,
      reportRequests.data,
    ],
  );

  // `tz` decides whether a report request in this timeline reads overdue.
  // Threaded rather than left to default: the same requests are counted by
  // `ReportRequestsSection`, and the two must not answer in different zones.
  const timeline = useMemo(() => buildCaseTimeline(artifacts), [artifacts, tz]);
  const totalCosts = artifacts.costs.reduce((s, c) => s + c.amount, 0);
  // The timeline folds eleven artifact queries — "loading" until they have
  // all settled, or the empty-state prompt lies while entries pop in.
  const timelineLoading =
    events.isLoading ||
    curePeriods.isLoading ||
    plans.isLoading ||
    forbearance.isLoading ||
    daca.isLoading ||
    legalActions.isLoading ||
    engagements.isLoading ||
    collateralRecoveries.isLoading ||
    costs.isLoading ||
    recoveries.isLoading ||
    reportRequests.isLoading;

  // ?artifact=<type>:<id> drawer deep link.
  const selectedArtifact = parseTimelineKey(params.get("artifact"));
  const openArtifact = (key: string) => {
    const u = new URLSearchParams(params);
    u.set("artifact", key);
    setParams(u, { replace: true });
  };
  const closeArtifact = () => {
    const u = new URLSearchParams(params);
    u.delete("artifact");
    setParams(u, { replace: true });
  };

  const curePeriod: CurePeriodDto | null =
    artifacts.curePeriods.length > 0
      ? artifacts.curePeriods[artifacts.curePeriods.length - 1]
      : null;

  return (
    <div
      className="flex flex-col gap-space-3"
      data-testid="defaults-workout-tab"
    >
      <CaseStatusBanner
        defaultCase={defaultCase}
        loanId={loanId}
        totalCosts={totalCosts}
        recoveries={artifacts.recoveries}
        events={artifacts.events}
      />

      <StageActionRail
        status={defaultCase.status}
        onAction={(a) => setAction({ kind: a })}
      />

      <div className="grid gap-space-3 lg:grid-cols-[minmax(0,1fr)_320px]">
        <CaseTimeline
          entries={timeline}
          isLoading={timelineLoading}
          onOpen={openArtifact}
        />
        <div className="flex flex-col gap-space-3">
          <SideRailCards artifacts={artifacts} onOpen={openArtifact} />
          <DwReportCard loanId={loanId} />
        </div>
      </div>

      <TimeLogSection caseId={caseId} loanId={loanId} />

      <HistoricalDefaultsList cases={historical} onSelect={onSelectHistorical} />

      <CurePeriodStickyPanel defaultCase={defaultCase} curePeriod={curePeriod} />

      <CaseActionModals
        caseId={caseId}
        loanId={loanId}
        currentRemediationLevel={defaultCase.currentRemediationLevel}
        action={action}
        onClose={() => setAction(null)}
      />

      <ArtifactDrawer
        artifacts={artifacts}
        selected={selectedArtifact}
        onClose={closeArtifact}
        onLaunchAction={(a) => {
          closeArtifact();
          setAction(a);
        }}
      />
    </div>
  );
}
